import { memo } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { BedDouble, MapPin, Maximize2, Building2, Calendar } from 'lucide-react'
import { PropertyFavoriteButton } from '@/components/property/PropertyFavoriteButton'
import { PropertyBadges } from '@/components/property/PropertyBadge'
import { OptimizedImage } from '@/components/ui/OptimizedImage'
import { trackPropertyClick, usePropertyImpression } from '@/lib/analytics/propertyEvents'
import { formatPrice, formatRelativeDate, cn } from '@/lib/utils'
import { formatPropertyArea, hasBedroomLabel, shouldShowPricePerSqft } from '@/lib/propertyArea'
import { PATHS } from '@/routes/paths'
import type { Property } from '@/types/property'

interface PropertyCardProps {
  property: Property
  variant?: 'default' | 'horizontal' | 'featured'
  className?: string
  noHover?: boolean
  imagePriority?: boolean
  source?: string
  resultSetKey?: string
  listingPosition?: number
}

function PropertyCardComponent({
  property,
  variant = 'default',
  className,
  noHover = false,
  imagePriority = false,
  source,
  resultSetKey,
  listingPosition,
}: PropertyCardProps) {
  const detailUrl = PATHS.propertyDetail(property.id, property.slug)
  const areaLabel = formatPropertyArea(property)
  const showBhk = hasBedroomLabel(property.bhk)
  const ppsf =
    typeof property.price === 'number' && shouldShowPricePerSqft(property) && property.sqft
      ? `₹ ${Math.round(property.price / property.sqft).toLocaleString('en-IN')}/sqft`
      : null
  const analytics = {
    propertyId: property.id,
    cityId: property.cityId,
    areaId: property.areaId,
    categoryId: property.categoryId,
    intent: property.intent,
    source,
    listingPosition,
  }
  const impressionRef = usePropertyImpression({ ...analytics, resultSetKey: resultSetKey ?? '' })
  const onOpen = () => {
    if (!resultSetKey) return
    trackPropertyClick(analytics)
  }

  if (variant === 'horizontal') {
    return (
      <Link
        ref={impressionRef}
        to={detailUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onOpen}
        className={cn(
          'group flex overflow-hidden rounded-2xl border border-neutral-100 bg-white transition-all hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-card-hover',
          className,
        )}
      >
        <div className="relative w-36 shrink-0 sm:w-48">
          <OptimizedImage
            src={property.image}
            alt={property.name}
            className="h-full w-full object-cover"
            priority={imagePriority}
          />
        </div>
        <div className="flex flex-1 flex-col justify-between p-4">
          <div>
            <p className="font-display text-lg font-bold text-primary-700">{formatPrice(property.price)}</p>
            <h3 className="mt-1 line-clamp-1 text-sm font-semibold text-neutral-900">{property.name}</h3>
            <p className="mt-0.5 flex items-center gap-1 text-xs text-neutral-500">
              <MapPin className="h-3 w-3" /> {property.location}
            </p>
          </div>
        </div>
      </Link>
    )
  }

  const card = (
      <Link
        ref={impressionRef}
        to={detailUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onOpen}
        className={cn(
          'group flex h-full flex-col overflow-hidden rounded-xl border border-neutral-100 bg-white md:rounded-2xl',
          !noHover && 'transition-all hover:border-primary-200 hover:shadow-card-hover',
        )}
      >
        <div className="relative aspect-4/3 shrink-0 overflow-hidden bg-neutral-100 md:aspect-16/10">
          <OptimizedImage
            src={property.image}
            alt={property.name}
            className={cn(
              'h-full w-full object-cover',
              !noHover && 'transition-transform duration-500 group-hover:scale-105',
            )}
            priority={imagePriority}
          />
          <PropertyFavoriteButton
            propertyId={property.id}
            className="absolute right-1.5 top-1.5 md:right-2.5 md:top-2.5"
            iconClassName="h-5 w-5 md:h-7 md:w-7"
          />
          <PropertyBadges
            property={property}
            compact
            showIntent={false}
            className="absolute left-1.5 top-1.5 md:left-2.5 md:top-2.5"
          />
        </div>

        <div className="flex flex-1 flex-col p-2 pb-2.5 md:p-3.5 md:pb-4">
          <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 md:gap-x-2">
            <span className="font-display text-sm font-bold text-primary-700 md:text-lg">
              {formatPrice(property.price)}
            </span>
            {ppsf && <span className="hidden text-[11px] text-neutral-400 md:inline">{ppsf}</span>}
          </div>

          <h3
            className={cn(
              'mt-0.5 line-clamp-2 min-h-[2.2em] font-display text-[11px] font-semibold leading-snug text-neutral-900 md:mt-1.5 md:min-h-[2.6em] md:text-sm',
              !noHover && 'group-hover:text-primary-600',
            )}
          >
            {property.name}
          </h3>

          <p className="mt-0.5 flex min-h-[1.25rem] items-center gap-1 text-[10px] text-neutral-500 md:mt-1 md:min-h-[1.35rem] md:text-xs">
            <MapPin className="h-3 w-3 shrink-0 text-primary-400 md:h-3.5 md:w-3.5" />
            <span className="line-clamp-1">{property.location}</span>
          </p>

          {(showBhk || areaLabel) && (
            <p className="mt-1 flex items-center gap-1.5 text-[10px] font-medium text-neutral-600 md:hidden">
              {showBhk ? (
                <>
                  <BedDouble className="h-3 w-3 shrink-0 text-primary-500" />
                  {property.bhk}
                </>
              ) : null}
              {showBhk && areaLabel ? <span className="text-neutral-300">·</span> : null}
              {areaLabel ? (
                <>
                  <Maximize2 className="h-3 w-3 shrink-0 text-primary-500" />
                  {areaLabel}
                </>
              ) : null}
            </p>
          )}

          <div className="mt-2.5 hidden flex-wrap items-center gap-2.5 text-[11px] font-medium text-neutral-600 md:flex">
            {showBhk ? (
              <span className="flex items-center gap-1">
                <BedDouble className="h-3.5 w-3.5 text-primary-500" /> {property.bhk}
              </span>
            ) : null}
            {areaLabel ? (
            <span className="flex items-center gap-1">
              <Maximize2 className="h-3.5 w-3.5 text-primary-500" /> {areaLabel}
            </span>
            ) : null}
            {property.floor && <span>{property.floor}</span>}
          </div>

          <div className="mt-1.5 space-y-0.5">
            {property.builder ? (
              <p className="hidden items-center gap-1 text-[11px] text-neutral-500 md:flex">
                <Building2 className="h-3 w-3 shrink-0" /> By {property.builder}
              </p>
            ) : null}

            <p className="hidden items-center gap-1 text-[11px] text-neutral-400 md:flex">
              <Calendar className="h-3 w-3 shrink-0" /> {formatRelativeDate(property.postedAt)}
            </p>
          </div>
        </div>
      </Link>
  )

  if (noHover) {
    return <div className={cn('h-full', className)}>{card}</div>
  }

  return (
    <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }} className={cn('h-full', className)}>
      {card}
    </motion.div>
  )
}

function propertyCardPropsEqual(prev: PropertyCardProps, next: PropertyCardProps) {
  return (
    prev.property.id === next.property.id &&
    prev.property.image === next.property.image &&
    prev.property.price === next.property.price &&
    prev.property.name === next.property.name &&
    prev.variant === next.variant &&
    prev.noHover === next.noHover &&
    prev.className === next.className &&
    prev.imagePriority === next.imagePriority
  )
}

export const PropertyCard = memo(PropertyCardComponent, propertyCardPropsEqual)
